// TreeNode.jsx
import { useState } from 'react';
import './TreeView.css';
import { CaretDown, CaretDownFill, CaretLeft, CaretLeftFill, CheckSquareFill, DashCircleFill, PencilSquare, Stop } from 'react-bootstrap-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCartPlus, faCheckSquare, faDotCircle, faFolderPlus, faPencilAlt, faPlus, faPlusCircle, faTimes } from '@fortawesome/free-solid-svg-icons';

/**
 * @typedef {object} Node
 * @property {string} id
 * @property {string} name
 * @property {string} [code]
 * @property {Node[]} [children]
 */

/**
 * @param {object} props
 * @param {Node} props.node
 * @param {Record<string, boolean>} props.expanded
 * @param {(id: string) => void} props.onToggleExpand
 * @param {(parentId: string, name: string, code?: string) => void} props.onAddNode
 * * @param {(parentId: string, name: string, code?: string) => void} props.onEditNode
 * @param {(id: string) => void} props.onRemoveNode
 * @param {(Node) =>void} props.onSelectNode
 * @param {string} props.search
 */
const TreeNode = ({
  node,
  expanded,
  onToggleExpand,
  onAddNode,
  onEditNode,
  onRemoveNode,
  onSelectNode,
  search,
}) => {
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [newName, setNewName] = useState("");
  const [newCode, setNewCode] = useState("");

  const isExpanded = expanded[node.id];
  const isNodeMatch = node.name?.toLowerCase().includes(search?.toLowerCase()) ||
    (node.code && node.code.toLowerCase().includes(search?.toLowerCase()));

  // هایلایت سرچ در name و code
  // تابع highlight باید در همین فایل یا جای دیگری تعریف شده باشد
  const highlight = (text, searchTerm) => {
    if (!searchTerm) return text;
    // اطمینان از اینکه کاراکترهای خاص regex escape شوند
    const escapedSearch = searchTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const regex = new RegExp(`(${escapedSearch})`, "gi");
    return text.replace(regex, "<mark>$1</mark>"); // استفاده از تگ mark برای هایلایت
  };

  // --- حذف گره با هشدار ---
  const handleDelete = (e) => {
    e.stopPropagation(); // جلوگیری از فعال شدن event والد
    setConfirmDelete(true);
  };
  const confirmDeleteNode = () => {
    onRemoveNode(node.id);
    setConfirmDelete(false);
  };
  const cancelDelete = () => {
    setConfirmDelete(false);
  }

  // --- اضافه کردن گره جدید ---
  const handleAdd = (e) => {
    e.stopPropagation();
    setShowAddForm((p) => !p);
  };
  const handleEdit = (e) => {
    e.stopPropagation();
    setNewName(node.name);
    setNewCode(node.code);
    setEditMode((p) => !p);
    setShowAddForm((p) => !p);
  }
  const confirmEdit = () => {
    if (!newName.trim()) return;
    onEditNode(node.id, newName, newCode);
    setNewName("");
    setNewCode("");
    setShowAddForm(false);
    setEditMode(false);
  }
  const confirmAdd = () => {
    if (!newName.trim()) return;
    onAddNode(node.id, newName, newCode);
    setNewName("");
    setNewCode("");
    setShowAddForm(false);
  };
  const cancelAdd = () => {
    setNewName("");
    setNewCode("");
    setShowAddForm(false);
  }

  const hasChildren = node.children && node.children.length > 0;

  return (
    <div className={`tree-node ${isNodeMatch ? 'highlighted' : ''}`}>
      {/* خط اصلی گره */}
      <div className="node-content">
        {hasChildren ? (
          <button
            onClick={() => onToggleExpand(node.id)}
            className="toggle-button"
          // aria-expanded={isExpanded}
          >
            {isExpanded ? <CaretDownFill size={18} /> : <CaretLeftFill size={18} />}
          </button>
        ) : (
          <span className="toggle-placeholder"><Stop /></span>
        )}

        <span
          className="node-name"
          onClick={() => onSelectNode?.(node)}
          dangerouslySetInnerHTML={{
            __html: highlight(node.name, search),
          }}
        />

        {node.code && (
          <span
            className="node-code"
            onClick={() => onSelectNode?.(node)}
            dangerouslySetInnerHTML={{
              __html: highlight(` (${node.code})`, search),
            }}
          />
        )}
        {typeof onSelectNode === "function" || showAddForm ?
          null :
          <div className='action-div'>
            <button
              className="action-button add-button tooltipBtn"
              onClick={handleAdd}
              data-tooltip="افزودن فرزند"
              aria-label="افزودن فرزند"
            >
              <FontAwesomeIcon icon={faPlusCircle} size='lg' color='#07886a' />
            </button>
            <button
              className="tooltipBtn action-button remove-button"
              data-tooltip="ویرایش گره"
              onClick={handleEdit}
              aria-label="ویرایش گره"
            >
              <FontAwesomeIcon icon={faPencilAlt} size='lg' color='orange' />
            </button>
            <button
              className="tooltipBtn action-button remove-button"
              data-tooltip="حذف گره"
              onClick={handleDelete}
              aria-label="حذف گره"
            >
              <FontAwesomeIcon icon={faTimes} size='lg' color='red' />
            </button>
          </div>}
      </div>

      {/* فرم افزودن فرزند */}
      {showAddForm && (
        <div className="add-node-form">
          <input
            type="text"
            placeholder="نام گره جدید"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            className="add-input"
          />
          <input
            type="text"
            placeholder="کد (اختیاری)"
            value={newCode}
            onChange={(e) => setNewCode(e.target.value)}
            className="add-input"
          />
          {editMode ?
          <FontAwesomeIcon icon={faPencilAlt} className='action-icon' onClick={confirmEdit} size="lg" color='orange' />:
          <FontAwesomeIcon icon={faCheckSquare} className='action-icon' onClick={confirmAdd} size="lg" color='green' />
          }
          <FontAwesomeIcon icon={faDotCircle} className='action-icon' onClick={cancelAdd} size="lg" color='red' />


        </div>
      )}

      {/* مودال تأیید حذف */}
      {confirmDelete && (
        <div className="modal-overlay">
          <div className="modal-content bodyStyle">
            <p className='modal-header'>آیا از حذف «{node.name}» مطمئن هستید؟</p>
            <div className="modal-actions">
              <button onClick={confirmDeleteNode} className="confirm-button">بله، حذف کن</button>
              <button onClick={cancelDelete} className="cancel-button">انصراف</button>
            </div>
          </div>
        </div>
      )}

      {/* نمایش فرزندان */}
      {isExpanded && hasChildren && (
        <div className="node-children">
          {node.children?.map((child) => (
            <TreeNode
              key={child.id}
              node={child}
              expanded={expanded}
              onToggleExpand={onToggleExpand}
              onAddNode={onAddNode}
              onRemoveNode={onRemoveNode}
              search={search}
              onSelectNode={onSelectNode}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default TreeNode;
